import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import ReportDateRange from '../../../components/ui/ReportDateRange';
import { downloadCsv } from '../../../utils/csv';
import { BandProduct, loadRestaurantReport, minutesText, money, reportCsvRows, RestaurantReport } from './restaurantReport';

// Reporte de restaurante: ticket por comensal, rotación de mesas, tiempos de cocina y productos por franja horaria
export const RestaurantReportPage = () => {
    const [range, setRange] = useState({ from: moment().format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') });
    const [report, setReport] = useState<RestaurantReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        loadRestaurantReport(range.from, range.to)
            .then(r => active && setReport(r))
            .catch(e => active && setError(e.message))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [range]);

    const handleRange = (start: Moment, end: Moment) => setRange({ from: start.format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD') });

    const kpi = (label: string, value: string, icon: string, hint?: string) => (
        <div className="col-6 col-lg-3">
            <div className="border rounded p-3 h-100 bg-white">
                <div className="small text-muted"><i className={`bi ${icon} me-1`} />{label}</div>
                <div className="fs-4 fw-semibold">{value}</div>
                {hint && <div className="small text-muted">{hint}</div>}
            </div>
        </div>
    );

    const productList = (list: BandProduct[], empty: string) => list.length === 0
        ? <div className="small text-muted">{empty}</div>
        : (
            <ol className="small ps-3 mb-0">
                {list.map(p => (
                    <li key={p.productId} className="d-flex justify-content-between">
                        <span className="text-truncate me-2">{p.name}</span>
                        <span className="text-nowrap text-muted">{p.quantity.toLocaleString('es-CR')} · {money(p.total)}</span>
                    </li>
                ))}
            </ol>
        );

    const s = report?.summary;

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
                <div>
                    <h3 className="mb-1">Reporte de restaurante</h3>
                    <p className="text-muted mb-0">Ticket por comensal, rotación de mesas, tiempos de cocina y productos por franja horaria.</p>
                </div>
                <div className="d-flex align-items-end gap-2">
                    <div>
                        <label className="form-label small text-muted mb-1">Fechas</label>
                        <ReportDateRange onChange={handleRange} minDate={moment().subtract(1, 'year')} maxSpan={{ months: 3 }} />
                    </div>
                    <button type="button" className="btn btn-outline-secondary" disabled={!report}
                        onClick={() => report && downloadCsv(`restaurante_${report.from}_${report.to}.csv`, reportCsvRows(report))}>
                        <i className="bi bi-download me-1" /> Exportar
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}

            {loading ? (
                <div className="text-center text-muted py-5"><span className="spinner-border spinner-border-sm me-2" />Cargando...</div>
            ) : report && s && (
                <>
                    <div className="row g-3 mb-4">
                        {kpi('Ventas', money(s.sales), 'bi-cash-stack', `${s.invoices} facturas`)}
                        {kpi('Ticket promedio', money(s.averageTicket), 'bi-receipt')}
                        {kpi('Promedio por comensal', money(s.averagePerGuest), 'bi-people',
                            s.guestCoverage !== null ? `${s.guests} comensales · ${s.guestCoverage} % de las facturas con comensales` : undefined)}
                        {kpi('Tiempo de cocina', minutesText(report.kitchen.average), 'bi-fire',
                            report.kitchen.tickets ? `p90 ${minutesText(report.kitchen.p90)} · ${report.kitchen.tickets} comandas` : 'Sin comandas listas')}
                    </div>

                    <div className="row g-3 mb-4">
                        {/* Mesas */}
                        <div className="col-12 col-xl-7">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="mb-3"><i className="bi bi-grid-3x3-gap me-1" />Rotación de mesas</h6>
                                    {report.tables.length === 0 ? <div className="small text-muted">No hay órdenes en mesa en estas fechas.</div> : (
                                        <div className="table-responsive">
                                            <table className="table table-sm align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Mesa</th>
                                                        <th className="text-end">Órdenes</th>
                                                        <th className="text-end">Por día</th>
                                                        <th className="text-end">Tiempo promedio</th>
                                                        <th className="text-end">Ventas</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {report.tables.map(t => (
                                                        <tr key={t.tableId}>
                                                            <td>{t.table}</td>
                                                            <td className="text-end">{t.orders}</td>
                                                            <td className="text-end">{t.turnsPerDay.toLocaleString('es-CR')}</td>
                                                            <td className="text-end">{minutesText(t.averageMinutes)}</td>
                                                            <td className="text-end">{money(t.sales)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    <div className="small text-muted mt-2">Tiempo desde que se abre la orden hasta que se factura. Las órdenes de más de 6 horas no cuentan.</div>
                                </div>
                            </div>
                        </div>

                        {/* Cocina */}
                        <div className="col-12 col-xl-5">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="mb-3"><i className="bi bi-fire me-1" />Tiempos de cocina</h6>
                                    {report.kitchen.tickets === 0 ? (
                                        <div className="small text-muted">No hay comandas marcadas como listas en la pantalla de cocina.</div>
                                    ) : (
                                        <>
                                            <table className="table table-sm mb-3">
                                                <thead><tr><th>Tiempo</th><th className="text-end">Comandas</th><th className="text-end">Promedio</th><th className="text-end">p90</th></tr></thead>
                                                <tbody>
                                                    {report.kitchen.courses.map(c => (
                                                        <tr key={c.course}>
                                                            <td>{c.name}</td>
                                                            <td className="text-end">{c.tickets}</td>
                                                            <td className="text-end">{minutesText(c.average)}</td>
                                                            <td className="text-end">{minutesText(c.p90)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <div className="small fw-semibold mb-1">Platos más lentos</div>
                                            {report.kitchen.slowest.length === 0
                                                ? <div className="small text-muted">Se muestran los platos con al menos 3 comandas.</div>
                                                : (
                                                    <ol className="small ps-3 mb-0">
                                                        {report.kitchen.slowest.map(p => (
                                                            <li key={p.name} className="d-flex justify-content-between">
                                                                <span>{p.name}</span>
                                                                <span className="text-muted">{minutesText(p.average)} · {p.tickets} comandas</span>
                                                            </li>
                                                        ))}
                                                    </ol>
                                                )}
                                        </>
                                    )}
                                    <div className="small text-muted mt-2">Desde que el mesero marcha a cocina hasta que la cocina la marca lista.</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Productos por franja */}
                    <h6 className="mb-2"><i className="bi bi-clock me-1" />Productos por franja horaria</h6>
                    {report.bands.length === 0 ? <div className="small text-muted mb-4">No hay ventas en estas fechas.</div> : (
                        <div className="row g-3 mb-4">
                            {report.bands.map(b => (
                                <div key={b.key} className="col-12 col-md-6 col-xl-4">
                                    <div className="card border-0 shadow-sm h-100">
                                        <div className="card-body">
                                            <div className="d-flex justify-content-between align-items-baseline mb-2">
                                                <h6 className="mb-0">{b.name} <span className="small text-muted fw-normal">{b.hours}</span></h6>
                                                <span className="small text-muted">{money(b.sales)}</span>
                                            </div>
                                            <div className="small text-success fw-semibold mb-1"><i className="bi bi-arrow-up me-1" />Más vendidos</div>
                                            {productList(b.top, 'Sin ventas.')}
                                            <div className="small text-danger fw-semibold mt-3 mb-1"><i className="bi bi-arrow-down me-1" />Menos vendidos</div>
                                            {productList(b.bottom, 'Todos los productos vendidos están entre los más vendidos.')}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
